import {
    Card,
    CardContent,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { FixedContractRow } from "./fixed-contract-row"
import { CostFigure } from "./cost-figure"

export function CostFixedContractsSection({ costResult, currencySymbol }) {
    const fixedContracts = costResult?.fixedContracts || []
    const capacity = costResult?.capacity
    // Capacity columns only appear once at least one contract has contracted days, so legacy
    // cards with no capacity configured keep the original table.
    const showCapacity = fixedContracts.some(hasContractedDays)
    const unassessedCount = showCapacity ? fixedContracts.filter(hasNoContractedDays).length : 0
    const total = fixedContracts.reduce(sumAmount, 0)
    const timeLabel = showCapacity ? "Required time" : "Hours covered"

    return (
        <If value={fixedContracts.length > 0}>
            <Card data-cy="fixed-contracts-section">
                <CardContent sx={{ p: 3 }}>
                    <Stack spacing={2}>
                        <Typography variant="h6">Fixed Annual Contracts</Typography>
                        <If value={unassessedCount > 0}>
                            <Typography variant="body2" color="warning.dark" data-cy="fixed-contracts-unassessed">
                                {getUnassessedMessage()}
                            </Typography>
                        </If>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Trade</TableCell>
                                        <TableCell align="right">Tasks covered</TableCell>
                                        <TableCell align="right">{timeLabel}</TableCell>
                                        <If value={showCapacity}>
                                            <TableCell align="right">Contracted days</TableCell>
                                            <TableCell align="right">Capacity</TableCell>
                                        </If>
                                        <TableCell align="right">Annual amount</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <Repeat
                                        list={fixedContracts}
                                        item={
                                            <FixedContractRow
                                                currencySymbol={currencySymbol}
                                                capacity={capacity}
                                                showCapacity={showCapacity}
                                            />
                                        }
                                    />
                                    <TableRow sx={{ bgcolor: "action.hover" }}>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600}>
                                                Subtotal
                                            </Typography>
                                        </TableCell>
                                        <TableCell />
                                        <TableCell />
                                        <If value={showCapacity}>
                                            <TableCell />
                                            <TableCell />
                                        </If>
                                        <TableCell align="right">
                                            <CostFigure
                                                value={total}
                                                currencySymbol={currencySymbol}
                                                fontWeight={600}
                                            />
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Stack>
                </CardContent>
            </Card>
        </If>
    )

    function sumAmount(sum, c) {
        return sum + (c.amount || 0)
    }

    function hasContractedDays(c) {
        return c.contractedDaysPerYear > 0
    }

    function hasNoContractedDays(c) {
        return !hasContractedDays(c)
    }

    function getUnassessedMessage() {
        const noun = unassessedCount === 1 ? "contract is" : "contracts are"
        return `${unassessedCount} fixed ${noun} not assessed because contracted days are missing. Capacity totals exclude this work.`
    }
}
